import type { MemberUsage, OrgUsageReportMember } from './types';

/**
 * A member's allowance, read the one way the backend defines it.
 *
 * Backend PR #327 split a seat's `budget_usd` into a PLAN part and a CREDITS part: spend draws from
 * the plan first, then from credits; the plan part resets each cycle, credits carry over and survive
 * a plan ending. This module is the only place that turns those four optional wire fields into
 * something a component may render, so that "the backend did not send a breakdown" and "the member
 * has $0 of credit" can never collapse into the same pixels.
 *
 * Every figure here is on the ENFORCED clock — the backend computes the split against
 * `spend_usd_cycle` — which is why `remainingUsd` from this module is what a budget meter must use,
 * not `budget − spend_usd_window`. The reporting month is for "what did October cost", not for
 * "how close is this person to being paused".
 */

type AllowanceSource = Pick<
  MemberUsage,
  'budget_usd' | 'plan_budget_usd' | 'plan_remaining_usd' | 'credit_balance_usd' | 'credit_remaining_usd'
> &
  Partial<Pick<MemberUsage, 'remaining_usd'>> &
  Partial<Pick<OrgUsageReportMember, 'spend_usd_cycle'>>;

export interface MemberAllowance {
  /** `budget_usd` — the ceiling LiteLLM enforces. */
  totalUsd: number;
  /**
   * What the member can still spend this cycle. From the backend split when present, from
   * `remaining_usd` on the rollup, else derived from cycle spend. `null` only when nothing on the
   * row can answer it.
   */
  remainingUsd: number | null;
  /** True when the backend sent the plan/credit split; the fields below are then real figures. */
  hasBreakdown: boolean;
  planBudgetUsd: number | null;
  planRemainingUsd: number | null;
  creditBalanceUsd: number | null;
  creditRemainingUsd: number | null;
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function memberAllowance(row: AllowanceSource): MemberAllowance {
  const hasBreakdown =
    isNumber(row.plan_budget_usd) &&
    isNumber(row.plan_remaining_usd) &&
    isNumber(row.credit_balance_usd) &&
    isNumber(row.credit_remaining_usd);

  let remainingUsd: number | null;
  if (hasBreakdown) {
    remainingUsd = row.plan_remaining_usd! + row.credit_remaining_usd!;
  } else if (isNumber(row.remaining_usd)) {
    remainingUsd = row.remaining_usd;
  } else if (isNumber(row.spend_usd_cycle)) {
    remainingUsd = Math.max(0, row.budget_usd - row.spend_usd_cycle);
  } else {
    remainingUsd = null;
  }

  return {
    totalUsd: row.budget_usd,
    remainingUsd,
    hasBreakdown,
    planBudgetUsd: hasBreakdown ? row.plan_budget_usd! : null,
    planRemainingUsd: hasBreakdown ? row.plan_remaining_usd! : null,
    creditBalanceUsd: hasBreakdown ? row.credit_balance_usd! : null,
    creditRemainingUsd: hasBreakdown ? row.credit_remaining_usd! : null,
  };
}

/**
 * Whether a seat with `budget_usd === 0` can still spend.
 *
 * Before #327 a zero budget was read as "uncapped" (`BudgetMeter` "No limit"), and separately an
 * ended plan left $0 — two opposite meanings of one value. With the split, an ended plan with
 * credits left has `budget_usd > 0` (the credits), so the only zero that is still ambiguous is
 * plan 0 AND credits 0. The backend has not shipped an `unlimited` flag (audit ask G3), so this
 * reports the ambiguity rather than resolving it: callers that know the seat's plan has ended render
 * "No allowance"; everyone else keeps today's "No limit".
 */
export function isZeroAllowance(allowance: MemberAllowance): boolean {
  return allowance.totalUsd <= 0;
}

/**
 * Sum a roster's allowances, keeping the breakdown only if EVERY row carries one.
 *
 * A partial sum — plan totals from the rows that have the split plus nothing from the rows that do
 * not — would understate credits and read as a real number. All-or-nothing is the only honest
 * aggregate.
 */
export interface RosterAllowance {
  totalUsd: number;
  remainingUsd: number | null;
  hasBreakdown: boolean;
  planBudgetUsd: number | null;
  creditBalanceUsd: number | null;
  creditRemainingUsd: number | null;
}

export function rosterAllowance(rows: AllowanceSource[]): RosterAllowance {
  const allowances = rows.map(memberAllowance);
  const hasBreakdown = allowances.length > 0 && allowances.every((a) => a.hasBreakdown);
  const everyRemaining = allowances.length > 0 && allowances.every((a) => a.remainingUsd !== null);
  const sum = (pick: (a: MemberAllowance) => number | null) =>
    allowances.reduce((acc, a) => acc + (pick(a) ?? 0), 0);

  return {
    totalUsd: sum((a) => a.totalUsd),
    remainingUsd: everyRemaining ? sum((a) => a.remainingUsd) : null,
    hasBreakdown,
    planBudgetUsd: hasBreakdown ? sum((a) => a.planBudgetUsd) : null,
    creditBalanceUsd: hasBreakdown ? sum((a) => a.creditBalanceUsd) : null,
    creditRemainingUsd: hasBreakdown ? sum((a) => a.creditRemainingUsd) : null,
  };
}
